import { startRegistration } from "@simplewebauthn/browser";
import { httpsCallableFromURL } from "firebase/functions";
import { clearChallenge } from "./clearChallenge.js";
import { FirebaseWebAuthnError } from "./FirebaseWebAuthnError.js";
import { handleVerifyFunctionResponse } from "./handleVerifyFunctionResponse.js";
/**
 * Asynchronously creates WebAuthn credentials associated with the signed-in user.
 *
 * @async
 *
 * @param auth - The {@link Auth} instance.
 * @param functions - The {@link Functions} instance.
 * @param name - An existing user identifier if FirebaseWebAuthn is configured as an MFA provider, or any recognizable value if FirebaseWebAuthn is your sole auth provider. With generic values consider passing something like `"${firstName} | Personal"` for users who share a passkey manager with others.
 * @param factor - An optional {@link WebAuthnUserCredentialFactor} to be required. Not passing a value attempts to link a primary credential.
 *
 * @returns
 *  A {@link UserCredential} when successful.
 * @throws
 *  {@link FirebaseWebAuthnError}
 */
export const linkWithPasskey = (auth, functions, name, factor) => auth
    .currentUser ? httpsCallableFromURL(functions, "/firebase-web-authn-api")({
    name: name,
    operation: "create registration challenge",
    registeringCredential: factor || "first",
})
    .then(({ data: functionResponse }) => "creationOptions" in functionResponse ? startRegistration({
    ...functionResponse.creationOptions,
    user: {
        ...functionResponse.creationOptions.user,
        id: functionResponse.creationOptions.user.id, // expecting string, actually uint8array :(
    }
}).then((registrationResponse) => httpsCallableFromURL(functions, "/firebase-web-authn-api")({
    registrationResponse: registrationResponse,
    operation: "verify registration",
}).then(({ data: functionResponse }) => handleVerifyFunctionResponse(auth, functionResponse), (firebaseError) => {
    throw new FirebaseWebAuthnError({
        code: firebaseError.code.replace("firebaseWebAuthn/", ""),
        message: firebaseError.message,
        method: "httpsCallableFromURL",
        operation: "verify registration",
    });
}), () => clearChallenge(functions).then(() => {
    throw new FirebaseWebAuthnError({
        code: "cancelled",
        message: "Cancelled by user.",
    });
})) : "code" in functionResponse ? (() => {
    throw new FirebaseWebAuthnError(functionResponse);
})() : (() => {
    throw new FirebaseWebAuthnError({
        code: "invalid",
        message: "Invalid function response.",
        operation: functionResponse.operation,
    });
})(), (firebaseError) => {
    throw new FirebaseWebAuthnError({
        code: firebaseError.code.replace("firebaseWebAuthn/", ""),
        message: firebaseError.message,
        method: "httpsCallableFromURL",
        operation: "create registration challenge",
    });
}) : (() => {
    throw new FirebaseWebAuthnError({
        code: "missing-auth",
        message: "No user is signed in.",
    });
})();
